import { AnimatePresence, motion, Variants } from "framer-motion";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";

import { sequentialLoadingState } from "~/atoms/sequential-loading";
import { useBreakpoint } from "~/contexts/breakpoint";
import CrossCountry from "~/types/cross-country";
import { basicAnimationStates } from "~/utils/animation-utils";
import Constants from "~/utils/constants";
import imageUtils from "~/utils/image-utils";
import productUtils from "~/utils/product-utils";
import TrackingUtils from "~/utils/tracking-utils";

import Container from "../common/container";
import Grid from "../common/grid";
import Icon from "../common/icon";
import Pane from "../common/pane";
import ProductActions from "../common/product-actions";
import NotifyMe from "../notify-me/notify-me";
import styles from "./product-fixed-pane.module.scss";
import { ShadeSelectorWrapper } from "./product-shade-selector";
import ProductStackedPrice from "./product-stacked-price";

const paneVariants: Variants = {
  visible: {
    opacity: 1,
    y: "0%",
    visibility: "visible",
  },
  hidden: {
    opacity: 0,
    y: "50%",
    visibility: "hidden",
  },
};

type Props = {
  root: CrossCountry.DetailedProduct;
  showFixedPanel: boolean;
  selectedShade: CrossCountry.ChildrenProduct;
  shades: CrossCountry.Product[];
};

export default function ProductFixedPane(props: Props) {
  const refNotifyMe = useRef<HTMLDivElement>(null);
  const t = useTranslations();
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const goccia = productUtils.getProductDroplet(props.selectedShade?.product_media?.media);

  const [shadesExpanded, setShadesExpanded] = useState(false);
  const [isNotifyMeOpen, setIsNotifyMeOpen] = useState<boolean>(false);
  const [shadeSelectorHeight, setShadeSelectorHeight] = useState(0);
  const [panelHeight, setPanelHeight] = useState(0);
  const sequentialLoadingReady = useRecoilValue(sequentialLoadingState);

  const URLRef = useRef<string | undefined>(router.asPath);
  const panelHeightRef = useRef<HTMLDivElement>(null);

  // Reset the notify me panel open state when the URL changes
  useEffect(() => {
    if (router.asPath !== undefined && URLRef.current && URLRef.current !== router.asPath) {
      setIsNotifyMeOpen(false);
      URLRef.current = router.asPath;
    }
  }, [setIsNotifyMeOpen, router.asPath]);

  useEffect(() => {
    if (panelHeightRef.current) {
      setPanelHeight(panelHeightRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    const usableNetContainer = document.getElementById(Constants.USABLENET_CONTAINER_KEY);

    if (sequentialLoadingReady.usablenet && usableNetContainer) {
      usableNetContainer.style.bottom =
        props.showFixedPanel && breakpoint === "mobile"
          ? `clamp(20px, calc(${
              props.shades && props.shades.length > 1 ? `${panelHeight + 20}px` : "80px"
            } + ${shadeSelectorHeight}px), calc(100vh - ${Constants.HEADER_HEIGHT_MOBILE}px))`
          : "20px";
    }

    return () => {
      if (usableNetContainer) {
        usableNetContainer.style.bottom = "";
      }
    };
  }, [
    breakpoint,
    props.shades,
    props.showFixedPanel,
    shadeSelectorHeight,
    sequentialLoadingReady.usablenet,
    panelHeight,
  ]);

  function shadeIsClicked(currentState: boolean): void {
    setShadesExpanded(currentState);
  }

  return (
    <motion.div
      className={styles.fixedContainer}
      variants={paneVariants}
      initial={props.showFixedPanel ? "visible" : "hidden"}
      animate={props.showFixedPanel ? "visible" : "hidden"}
      aria-hidden
    >
      <Container>
        <Grid>
          <Pane ref={panelHeightRef} className={styles.fixedPane}>
            {props.shades && props.shades.length > 1 && (
              <div className={styles.shadesHeader}>
                {goccia ? (
                  <Image
                    className={styles.shadeImage}
                    alt={props.selectedShade.display_value}
                    src={goccia.url}
                    height={16}
                    width={16}
                    loader={imageUtils.evaImageLoader}
                  />
                ) : null}
                <p className={styles.shadeColor}>{props.selectedShade?.color}</p>
                <AnimatePresence mode="wait">
                  {shadesExpanded ? (
                    <motion.button
                      initial={basicAnimationStates.off}
                      animate={basicAnimationStates.on}
                      exit={basicAnimationStates.off}
                      onClick={() => setShadesExpanded(false)}
                      className={styles.close}
                      aria-label={t("generic.close")}
                    >
                      <Icon name="close" />
                    </motion.button>
                  ) : (
                    !isNotifyMeOpen && (
                      <motion.button
                        initial={basicAnimationStates.off}
                        animate={basicAnimationStates.on}
                        exit={basicAnimationStates.off}
                        onClick={() => setShadesExpanded(true)}
                        className={styles.shadesCount}
                      >
                        {t("generic.changecount", { count: props.shades?.length })}
                      </motion.button>
                    )
                  )}
                </AnimatePresence>
              </div>
            )}
            {props.shades && props.shades.length > 1 && (
              <ShadeSelectorWrapper
                root={props.root}
                selectedShade={props.selectedShade}
                shades={props.shades}
                open={shadesExpanded}
                onClose={() => setShadesExpanded(false)}
                className={styles.shadesSelector}
                setShadeSelectorHeight={setShadeSelectorHeight}
                shadeIsClicked={shadeIsClicked}
              />
            )}
            <div className={styles.purchaseInfo}>
              {/* Notify Me Form */}
              {isNotifyMeOpen && (
                <NotifyMe
                  ref={refNotifyMe}
                  className={styles.notifyMeForm}
                  productId={props.selectedShade.product_id}
                  onClose={() => setIsNotifyMeOpen(false)}
                  showSubmitButton={false}
                  showCloseIcon
                />
              )}

              <div className={styles.actionsContainer}>
                <ProductStackedPrice product={props.selectedShade} />

                <ProductActions
                  hasPrice={productUtils.hasValidDisplayPrice(props.selectedShade)}
                  productId={props.selectedShade.product_id}
                  outOfStock={!props.selectedShade.has_stock}
                  onClickNotifyMe={() => {
                    if (!isNotifyMeOpen) {
                      return setIsNotifyMeOpen(true);
                    }

                    // if notify me is open, clicking on the button submits the form contained <NotifyMe>
                    refNotifyMe.current
                      ?.querySelector("form")
                      ?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
                  }}
                  actionsContext="pdp"
                  trackingData={TrackingUtils.mapEcomItem(props.root, props.selectedShade, {})}
                  maxQuantity={props.selectedShade.ecomm_max_items_per_order}
                />
              </div>
            </div>
          </Pane>
        </Grid>
      </Container>
    </motion.div>
  );
}
